import { ChakraProvider, Box } from '@chakra-ui/react';
import { useEffect, useState, useMemo, useRef } from 'react';
import theme from './theme';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LocationProvider, useLocationPath } from './contexts/location';
import LocationBar from './components/LocationBar';
import Navigation from './components/Navigation';
import { AuthProvider, useAuth } from './contexts/auth';
import EmailConfirmationBanner from './components/EmailConfirmationBanner';
import { WorkspaceProvider } from './contexts/workspace';
import { NavigationProvider, useNavigation } from './contexts/navigation';
import { LayoutProvider, useLayout } from './contexts/layout.tsx';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import { lazyLoadRoute } from './utils/dynamicImport';

// Lazy loaded components with optimized loading
const Dashboard = lazyLoadRoute(() => import('./pages/Dashboard'), 'Dashboard');
const Landing = lazyLoadRoute(() => import('./pages/Landing'), 'Landing');
const Contact = lazyLoadRoute(() => import('./pages/Contact'), 'Contact');
const Privacy = lazyLoadRoute(() => import('./pages/Privacy'), 'Privacy');
const Terms = lazyLoadRoute(() => import('./pages/Terms'), 'Terms');
const Plans = lazyLoadRoute(() => import('./pages/Plans'), 'Plans');
const ResourceLimit = lazyLoadRoute(() => import('./pages/ResourceLimit'), 'ResourceLimit');
const ItemScanning = lazyLoadRoute(() => import('./pages/ItemScanning'), 'ItemScanning');
const ItemSearch = lazyLoadRoute(() => import('./pages/ItemSearch'), 'ItemSearch');
const Billing = lazyLoadRoute(() => import('./pages/Billing'), 'Billing');
const EbayListingPage = lazyLoadRoute(() => import('./pages/ItemSearch/EbayListingPage'), 'EbayListingPage');
const PlanChangeSuccessConfirmation = lazyLoadRoute(() => import('./pages/Billing/PlanChangeSuccessConfirmation'), 'PlanChangeSuccessConfirmation');
const PlanChangeUserConfirmation = lazyLoadRoute(() => import('./pages/Billing/PlanChangeUserConfirmation'), 'PlanChangeUserConfirmation');
const Login = lazyLoadRoute(() => import('./pages/Login'), 'Login');
const Register = lazyLoadRoute(() => import('./pages/Register'), 'Register');
const ChangePassword = lazyLoadRoute(() => import('./pages/ChangePassword'), 'ChangePassword');
const DeleteAccount = lazyLoadRoute(() => import('./pages/ChangePassword/DeleteAccount'), 'DeleteAccount');
const OAuthCallback = lazyLoadRoute(() => import('./pages/OAuth/OAuthCallback'), 'OAuthCallback');
const UnlinkedAccount = lazyLoadRoute(() => import('./pages/OAuth/UnlinkedAccount'), 'UnlinkedAccount');
const RequestReset = lazyLoadRoute(() => import('./pages/PasswordReset').then(module => ({ default: module.RequestReset })), 'RequestReset');
const ResetPassword = lazyLoadRoute(() => import('./pages/PasswordReset').then(module => ({ default: module.ResetPassword })), 'ResetPassword');
const EmailConfirmation = lazyLoadRoute(() => import('./pages/EmailConfirmation'), 'EmailConfirmation');
const AdminPanel = lazyLoadRoute(() => import('./pages/AdminPanel'), 'AdminPanel');
const Reports = lazyLoadRoute(() => import('./pages/Reports'), 'Reports');
const NotFound = lazyLoadRoute(() => import('./pages/NotFound'), 'NotFound');
const NotificationSettings = lazyLoadRoute(() => import('./pages/NotificationSettings'), 'NotificationSettings');
const UseCases = lazyLoadRoute(() => import('./pages/Marketing/UseCases'), 'UseCases');
const ItemTree = lazyLoadRoute(() => import('./pages/ItemTree'), 'ItemTree');

interface AppContentProps {
  topmargin: {
    base: string;
    md: string;
  };
  confirmBannerIsDismissed: boolean;
  setConfirmBannerIsDismissed: (dismissed: boolean) => void;
}

const AppContent = ({ topmargin, confirmBannerIsDismissed, setConfirmBannerIsDismissed }: AppContentProps) => {
  const { user } = useAuth() as { user: { email: string; email_confirmed: boolean } | null };
  const { isNavOpen, dragTransform } = useNavigation() as { isNavOpen: boolean; dragTransform: string | undefined };
  const { path } = useLocationPath() as { path: { text: string; url: string }[] };
  const { setLayout } = useLayout();

    
  // Use useRef to track previous render values
  const prevValuesRef = useRef<{
    pathLength: number;
    confirmBannerIsDismissed: boolean | null;
    emailConfirmed: boolean | null | undefined;
  }>({
    pathLength: -1,
    confirmBannerIsDismissed: null,
    emailConfirmed: null
  });
  
  // Update the layout context with the topmargin ONLY when values actually change
  useEffect(() => {
    const currentValues = {
      pathLength: path.length,
      confirmBannerIsDismissed: confirmBannerIsDismissed,
      emailConfirmed: user?.email_confirmed
    };
    
    // Only update if actual values changed
    if (prevValuesRef.current.pathLength !== currentValues.pathLength ||
        prevValuesRef.current.confirmBannerIsDismissed !== currentValues.confirmBannerIsDismissed ||
        prevValuesRef.current.emailConfirmed !== currentValues.emailConfirmed) {
      
      // Update our ref with current values
      prevValuesRef.current = currentValues;
      
      // Only now update the layout
      setLayout(topmargin);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path.length, confirmBannerIsDismissed, user?.email_confirmed, topmargin]);
  
  return (
  <>
  <Box
      left="0"
      top="0"
      w={{ base: "100%", md: isNavOpen ? "calc(100% - 250px)" : "calc(100% - 50px)" }}
      marginLeft={0}
      transform={{ "md": dragTransform || isNavOpen ? "translateX(250px)" : "translateX(50px)", 
                   "base": dragTransform || isNavOpen ? "translateX(250px)" : "translateX(0px)"}}
      transition="width 0.3s ease-in-out, transform 0.3s ease-in-out"
      overflowX="hidden"
      overflowY="visible"
      position="relative"
    >

      <Box
        position="fixed"
        zIndex="1000"
        top={0}
        left={0}
        right={0}
        bg="white"
        _dark={{ bg: "gray.800" }}
        borderBottomWidth="1px"
        display="flex"
        flexDirection="column"
      >
        <LocationBar path={path} />
        {user && !user.email_confirmed && (
          <EmailConfirmationBanner email={user.email} isDismissed={confirmBannerIsDismissed} setIsDismissed={setConfirmBannerIsDismissed} />
        )}
      </Box>

      <Box
        mt={topmargin}
        overflowY="visible"
        h={{ md: `calc(100vh - ${topmargin.md})`, base: `calc(100vh - ${topmargin.base})` }}
        maxH={{ md: `calc(100vh - ${topmargin.md})`, base: `calc(100vh - ${topmargin.base})` }}
      >
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/use-cases/*" element={<UseCases />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/oauth-callback/:provider" element={<OAuthCallback />} />
            <Route path="/oauth/unlinked" element={<UnlinkedAccount />} />
            <Route path="/forgot-password" element={<RequestReset />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/confirm-email" element={<EmailConfirmation />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/scan/*" element={
              <ProtectedRoute>
                <ItemScanning />
              </ProtectedRoute>
            } />
            <Route path="/search" element={
              <ProtectedRoute>
                <ItemSearch />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            } />
            <Route path="/change-password" element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            } />
            <Route path="/delete-account" element={
              <ProtectedRoute>
                <DeleteAccount />
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            } />
            <Route path="/plans" element={<Plans />} />
            <Route path="/resource-limit" element={<ResourceLimit />} />
            <Route path="/billing" element={
              <ProtectedRoute>
                <Billing />
              </ProtectedRoute>
            } />
            <Route path="/billing/confirm" element={
              <ProtectedRoute>
                <PlanChangeUserConfirmation />
              </ProtectedRoute>
            } />
            {/* Handle billing success without ProtectedRoute to preserve URL params */}
            <Route path="/billing/success" element={<PlanChangeSuccessConfirmation />} />
            <Route path="/notifications" element={
              <ProtectedRoute>
                <NotificationSettings />
              </ProtectedRoute>
            } />
            <Route path="/items" element={
              <ProtectedRoute>
                <ItemTree />
              </ProtectedRoute>
            } />
            <Route path="/ebay-listing" element={
              <ProtectedRoute>
                <EbayListingPage />
              </ProtectedRoute>
            } />
            
            {/* 404 catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>
      </Box>
      </>
  );
};

/**
 * Main application component with context providers
 * @function App
 * @returns The root component of the application
 */
export default function App() {
  return (
    <ChakraProvider theme={theme}>
      <Router>
        <LocationProvider>
          <AuthProvider>
            <WorkspaceProvider>
              <NavigationProvider>
                <LayoutProvider>
                  <AppWithContexts />
                </LayoutProvider>
              </NavigationProvider>
            </WorkspaceProvider>
          </AuthProvider>
        </LocationProvider>
      </Router>
    </ChakraProvider>
  );
}

/**
 * Component that uses context hooks after all providers are in place
 */
function AppWithContexts() {
  const { path } = useLocationPath() as { path: { text: string; url: string }[] };
  const { user } = useAuth() as { user: { email: string; email_confirmed: boolean } | null };
  const [confirmBannerIsDismissed, setConfirmBannerIsDismissed] = useState(false);

  // Calculate topmargin with useMemo to avoid recalculations
  const topmargin = useMemo(() => {
    return path.length > 0 || !confirmBannerIsDismissed || (user && !user.email_confirmed && confirmBannerIsDismissed)
      ? { base: confirmBannerIsDismissed ? "30px" : "120px", md: confirmBannerIsDismissed ? "30px" : "90px" }
      : {base: "0px", md: "0px"};
  }, [path.length, confirmBannerIsDismissed, user?.email_confirmed]);

  const navmargin = useMemo(() => {
    return path.length > 0 || !confirmBannerIsDismissed || (user && !user.email_confirmed && confirmBannerIsDismissed)
      ? { base: "55px", md: "0px" }
      : {base: "0px", md: "0px"};
  }, [path.length, confirmBannerIsDismissed, user?.email_confirmed]);

  return (
    <>
      <Navigation topmargin={navmargin} />
      <AppContent 
        topmargin={topmargin}
        confirmBannerIsDismissed={confirmBannerIsDismissed}
        setConfirmBannerIsDismissed={setConfirmBannerIsDismissed}
      />
    </>
  );
}
